import React, { useState, useEffect, useCallback } from 'react';
import { coinAnalysisService } from '../services/coinAnalysisService';

interface CoinAnalysisData {
  instId: string;
  baseCcy: string;
  currentPrice: number;
  marketCap?: number;
  totalSupply?: number;
  circulatingSupply?: number;
  basicInfo?: string;
  projectDescription?: string;
  useCase?: string;
  technology?: string;
  communityActivity?: string;
  socialSentiment?: string;
  developerActivity?: string;
  adoptionRate?: string;
  riskAnalysis?: string;
  liquidityRisk?: string;
  volatilityRisk?: string;
  regulatoryRisk?: string;
  technicalRisk?: string;
  marketRisk?: string;
  overallRiskScore?: number;
  investmentAdvice?: string;
  targetPrice?: number;
  riskLevel?: string;
  analysisDate: string;
}

interface CoinAnalysisDetailProps {
  instId: string;
  onBack: () => void;
}

const CoinAnalysisDetail: React.FC<CoinAnalysisDetailProps> = ({ instId, onBack }) => {
  const [analysisData, setAnalysisData] = useState<CoinAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalysisData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await coinAnalysisService.getCoinAnalysisDetail(instId);
      setAnalysisData(response);
    } catch (err: any) {
      console.error('获取币对分析数据失败:', err);
      setError(err.response?.data?.message || err.message || '获取分析数据失败');
    } finally {
      setLoading(false);
    }
  }, [instId]);

  useEffect(() => {
    fetchAnalysisData();
  }, [fetchAnalysisData]);

  const parseJsonData = (jsonString: string) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  // 简单的Markdown到HTML转换函数
  const markdownToHtml = (markdown: string): string => {
    let html = markdown
      // 标题
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-white mt-6 mb-3">$1</h3>')
      .replace(
        /^## (.*$)/gim,
        '<h2 class="text-xl font-bold text-white mt-8 mb-4 border-b border-white/20 pb-2">$1</h2>',
      )
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-white mt-8 mb-6">$1</h1>')
      // 粗体
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
      // 列表项
      .replace(/^- (.*$)/gim, '<li class="text-gray-200 ml-4 mb-1">• $1</li>')
      // 换行
      .replace(/\n\n/g, '</p><p class="text-gray-200 mb-4">')
      .replace(/\n/g, '<br>')
      // 包装段落
      .replace(/^(?!<[h|l])/gm, '<p class="text-gray-200 mb-4">')
      .replace(/(?<!>)$/gm, '</p>')
      // 清理多余的标签
      .replace(/<p class="text-gray-200 mb-4"><\/p>/g, '')
      .replace(/<p class="text-gray-200 mb-4"><br><\/p>/g, '');

    // 列表包装 - 使用更兼容的方式
    const lines = html.split('\n');
    let inList = false;
    let result = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.includes('<li class="text-gray-200 ml-4 mb-1">')) {
        if (!inList) {
          result += '<ul class="list-none space-y-1 mb-4">';
          inList = true;
        }
        result += line + '\n';
      } else {
        if (inList) {
          result += '</ul>';
          inList = false;
        }
        result += line + '\n';
      }
    }

    if (inList) {
      result += '</ul>';
    }

    return result;
  };

  const formatDocument = () => {
    if (!analysisData) return '';

    // 解析原始JSON数据
    const basicInfo = analysisData.basicInfo ? parseJsonData(analysisData.basicInfo) : null;
    const communityActivity = analysisData.communityActivity ? parseJsonData(analysisData.communityActivity) : null;
    const riskAnalysis = analysisData.riskAnalysis ? parseJsonData(analysisData.riskAnalysis) : null;

    let document = `# ${analysisData.baseCcy} (${analysisData.instId}) 深度分析报告\n\n`;
    document += `**分析时间**: ${new Date(analysisData.analysisDate).toLocaleString('zh-CN')}\n`;
    document += `**当前价格**: $${Number(analysisData.currentPrice).toFixed(8)}\n`;
    if (analysisData.marketCap) {
      document += `**市值**: $${Number(analysisData.marketCap).toLocaleString()}\n`;
    }
    if (analysisData.totalSupply) {
      document += `**总供应量**: ${Number(analysisData.totalSupply).toLocaleString()}\n`;
    }
    if (analysisData.circulatingSupply) {
      document += `**流通供应量**: ${Number(analysisData.circulatingSupply).toLocaleString()}\n`;
    }
    document += '\n---\n\n';

    // 基础信息部分 - 从原始JSON数据中提取
    document += '## 📋 项目基础信息\n\n';
    if (basicInfo) {
      // 处理项目描述
      if (basicInfo['项目描述']) {
        if (typeof basicInfo['项目描述'] === 'object') {
          const desc = basicInfo['项目描述'];
          if (desc['背景']) document += `### 项目背景\n${desc['背景']}\n\n`;
          if (desc['目的']) document += `### 项目目的\n${desc['目的']}\n\n`;
        } else {
          document += `### 项目描述\n${basicInfo['项目描述']}\n\n`;
        }
      }

      // 处理用例和应用场景
      if (basicInfo['用例和应用场景']) {
        if (typeof basicInfo['用例和应用场景'] === 'object') {
          const useCase = basicInfo['用例和应用场景'];
          if (useCase['主要用途']) document += `### 主要用途\n${useCase['主要用途']}\n\n`;
          if (useCase['应用场景'] && Array.isArray(useCase['应用场景'])) {
            document += `### 应用场景\n${useCase['应用场景'].join('\n- ')}\n\n`;
          }
        } else if (Array.isArray(basicInfo['用例和应用场景'])) {
          document += `### 用例和应用场景\n${basicInfo['用例和应用场景'].join('\n- ')}\n\n`;
        }
      }

      // 处理技术特点
      if (basicInfo['技术特点']) {
        if (typeof basicInfo['技术特点'] === 'object') {
          const tech = basicInfo['技术特点'];
          Object.keys(tech).forEach((key) => {
            if (typeof tech[key] === 'string') {
              document += `### ${key}\n${tech[key]}\n\n`;
            } else if (Array.isArray(tech[key])) {
              document += `### ${key}\n${tech[key].join('\n- ')}\n\n`;
            }
          });
        }
      }
    }

    // 社区活动部分 - 从原始JSON数据中提取
    document += '## 🌐 社区活跃度分析\n\n';
    if (communityActivity) {
      // 社交媒体情绪
      if (communityActivity['社交媒体情绪']) {
        const sentiment = communityActivity['社交媒体情绪'];
        if (typeof sentiment === 'object') {
          Object.keys(sentiment).forEach((platform) => {
            if (typeof sentiment[platform] === 'object') {
              const platformData = sentiment[platform];
              document += `### ${platform}情绪\n`;
              Object.keys(platformData).forEach((key) => {
                document += `**${key}**: ${platformData[key]}\n`;
              });
              document += '\n';
            }
          });
        }
      }

      // 开发者活跃度
      if (communityActivity['开发者活跃度']) {
        const devActivity = communityActivity['开发者活跃度'];
        if (typeof devActivity === 'object') {
          Object.keys(devActivity).forEach((key) => {
            if (typeof devActivity[key] === 'object') {
              document += `### ${key}\n`;
              Object.keys(devActivity[key]).forEach((subKey) => {
                document += `**${subKey}**: ${devActivity[key][subKey]}\n`;
              });
              document += '\n';
            }
          });
        }
      }

      // 采用率分析
      if (communityActivity['采用率分析']) {
        const adoption = communityActivity['采用率分析'];
        if (typeof adoption === 'object') {
          Object.keys(adoption).forEach((key) => {
            if (typeof adoption[key] === 'object') {
              document += `### ${key}\n`;
              Object.keys(adoption[key]).forEach((subKey) => {
                document += `**${subKey}**: ${adoption[key][subKey]}\n`;
              });
              document += '\n';
            } else {
              document += `### ${key}\n${adoption[key]}\n\n`;
            }
          });
        }
      }
    }

    // 风险分析部分 - 从原始JSON数据中提取
    document += '## ⚠️ 风险分析\n\n';
    if (riskAnalysis) {
      const riskTypes = ['流动性风险', '波动性风险', '监管风险', '技术风险', '市场风险'];

      riskTypes.forEach((riskType) => {
        if (riskAnalysis[riskType]) {
          const risk = riskAnalysis[riskType];
          if (typeof risk === 'object') {
            document += `### ${riskType}\n`;
            Object.keys(risk).forEach((key) => {
              document += `**${key}**: ${risk[key]}\n`;
            });
            document += '\n';
          } else {
            document += `### ${riskType}\n${risk}\n\n`;
          }
        }
      });

      // 投资建议
      if (riskAnalysis['投资建议']) {
        document += `### 投资建议\n${riskAnalysis['投资建议']}\n\n`;
      }

      // 风险等级
      if (riskAnalysis['风险等级']) {
        document += `### 风险等级\n${riskAnalysis['风险等级']}\n\n`;
      }

      // 综合风险评分
      if (riskAnalysis['综合风险评分']) {
        document += `### 综合风险评分\n${riskAnalysis['综合风险评分']}/10\n\n`;
      }

      // 目标价格
      if (riskAnalysis['目标价格']) {
        document += `### 目标价格\n$${riskAnalysis['目标价格']}\n\n`;
      }
    }

    return document;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>加载分析数据中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">加载失败</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button onClick={onBack} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            返回
          </button>
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold mb-2">暂无分析数据</h2>
          <p className="text-gray-300 mb-6">该币对尚未进行AI分析</p>
          <button onClick={onBack} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={onBack}
            className="group flex items-center justify-center w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
          >
            <svg
              className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-white">{analysisData.baseCcy} 分析报告</h1>
          </div>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 pt-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 shadow-2xl">
            <div className="prose prose-invert prose-sm max-w-none">
              <div
                className="text-gray-100 leading-relaxed text-sm"
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(formatDocument()),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoinAnalysisDetail;
